import Button from '@/components/Button.jsx';
import { BsList } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import sendToast from '@/lib/sendToast.js';
import setPath from '@/lib/setPath';
import { getWorkflowsMetadata, getWorkflowSnapshots, postWorkflowSnapshot } from '@/redux/actions/gravityActions.js';
import { setDrawerState } from '@/redux/actions/uiActions/drawerActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { useAnalytics } from '@/hooks/useAnalytics.js';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import selectors from '@/redux/selectors/selectors.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useResponsive from '@/hooks/useResponsive.js';
import setQuery from '@/lib/setQuery';

const WorkflowPublishButton = React.memo(() => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const { trackEvent } = useAnalytics();
  const isDraft = useSelector((state) => selectors.param(state, 'workflowVersion') === 'draft');
  const hasNoWorkflowSnapshots = useSelector((state) => workflowScreenSelectors.activeWorkflowSnapshots(state)?.length === 0);
  const canEditWorkflow = useSelector(workflowScreenSelectors.activeWorkflowIsEditable);
  const { tablet } = useResponsive();

  const workflowId = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.id);
  const workflowPublishedSnapshotId = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.publishedSnapshotId);
  const changesSincePublish = useSelector(memoizeSelector('workflowSnapshotDiffs', workflowScreenSelectors.workflowSnapshotDiffs));
  const schedulePublishPolicy = useSelector(workflowScreenSelectors.activeWorkflowSchedulePublishPolicy);

  const wasNeverPublished = useMemo(() => !loadingSnapshots && hasNoWorkflowSnapshots, [loadingSnapshots, hasNoWorkflowSnapshots]);
  const canPublish = useMemo(() => {
    return !(!isDraft || isPublishing || !canEditWorkflow);
  }, [isDraft, isPublishing, canEditWorkflow]);

  const handleSnapshotModalConfirm = async (data) => {
    if (data?.isPublished === true) {
      setIsPublishing(true);
      setQuery({ isPublishing: true });
    }
    await waitForSyncReady();
    trackEvent('workflow-publishSnapshot');
    const newVersion = data?.version;
    postWorkflowSnapshot({ workflowId, snapshot: data, changeCount: changesSincePublish.length }, (result, error) => {
      setIsPublishing(false);
      setQuery({ isPublishing: undefined });
      if (error) {
        sendToast({ type: 'error', message: error?.message || 'The workflow could not be published.' });
        return;
      }
      if (newVersion) {
        getWorkflowsMetadata();
        sendToast({
          duration: 3000,
          message: `Version ${newVersion} was published!`,
          action: {
            label: 'View Published',
            onClick: () => {
              setPath(`/workflows/${workflowId}/${newVersion}`);
            }
          }
        });
      }
    });
  };

  const handlePublish = () => {
    // Same policy the publish route enforces: an incomplete Schedule trigger never fires, so
    // say what is missing here instead of opening the version modal for a request that fails.
    if (!schedulePublishPolicy.success) {
      sendToast({ type: 'error', message: schedulePublishPolicy.message });
      return;
    }
    setModalState('snapshot', {
      open: true,
      mode: 'add',
      onChange: (data) => handleSnapshotModalConfirm(data)
    });
  };

  const handlePublishDrawerOpen = (e) => {
    trackEvent('workflow-openPublishDrawer');
    e.preventDefault();
    e.stopPropagation();
    setDrawerState('snapshots', {
      open: true
    });
  };

  const buttonActions = !wasNeverPublished ? [{ icon: isPublishing || loadingSnapshots ? Spinner : BsList, onClick: handlePublishDrawerOpen }] : [];

  useEffect(() => {
    if (workflowId && hasNoWorkflowSnapshots) {
      setLoadingSnapshots(true);
      getWorkflowSnapshots({ workflowId }, () => setLoadingSnapshots(false));
    }
  }, [workflowId]);

  if (!isDraft) {
    return null;
  }

  return (
    <Button //
      className={'primary publish-button'}
      onClick={handlePublish}
      value={workflowPublishedSnapshotId}
      disabled={!canPublish}
      loading={isPublishing}
      actions={buttonActions}>
      {tablet ? 'Publish' : 'Publish Workflow'}
    </Button>
  );
});

WorkflowPublishButton.displayName = 'WorkflowPublishButton';

export default WorkflowPublishButton;
